import React, { useState, useEffect } from 'react';

const Profile = () => {
  const [adminData, setAdminData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Track window width for responsive styling
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  useEffect(() => {
    const fetchProfile = async () => {
      const userId = localStorage.getItem('user_id');
      const token = localStorage.getItem('token');

      if (!userId || !token) {
        setError('User not authenticated');
        setIsLoading(false);
        return;
      }

      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${token}`);

      const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow"
      };

      try {
        const response = await fetch(`https://api.rtoxpert.com/api/users/profile/${userId}`, requestOptions);
        const result = await response.json();
        if (response.ok) {
          setAdminData(result.user);
          setFormData({
            name: result.user.name || '',
            email: result.user.email || '',
            date_of_birth: result.user.date_of_birth ? result.user.date_of_birth.split('T')[0] : '',
            city: result.user.city || '',
            state: result.user.state || '',
            country: result.user.country || '',
            profile_picture: result.user.profile_picture || ''
          });
        } else {
          setError(result.message || 'Failed to fetch profile');
        }
      } catch (error) {
        console.error(error);
        setError('An error occurred while fetching profile');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateProfile = async () => {
    if (!formData.name || !formData.email) {
      setUpdateError('Name and email are required');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setUpdateError('Invalid email format');
      return;
    }

    setUpdateLoading(true);
    setUpdateError(null);

    const userId = localStorage.getItem('user_id');
    const token = localStorage.getItem('token');

    if (!userId || !token) {
      setUpdateError('User not authenticated');
      setUpdateLoading(false);
      return;
    }

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      name: formData.name,
      email: formData.email,
      date_of_birth: formData.date_of_birth || undefined,
      city: formData.city || undefined,
      state: formData.state || undefined,
      country: formData.country || undefined,
      profile_picture: formData.profile_picture || undefined
    });

    const requestOptions = {
      method: "PUT",
      headers: myHeaders,
      body: raw,
      redirect: "follow"
    };

    try {
      const response = await fetch(`https://api.rtoxpert.com/api/users/profile/${userId}`, requestOptions);
      const result = await response.json();
      if (response.ok) {
        setAdminData((prev) => ({
          ...prev,
          ...result.user
        }));
        setIsEditing(false);
      } else {
        setUpdateError(result.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error(error);
      setUpdateError('An error occurred while updating profile');
    } finally {
      setUpdateLoading(false);
    }
  };

  if (isLoading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '100px' }}>
      <i className="fas fa-circle-notch fa-spin" style={{ fontSize: '30px', color: 'var(--primary)' }}></i>
    </div>
  );

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: isMobile ? '10px' : '20px',
        textAlign: 'center'
      }}>
        <p style={{ color: 'red', fontSize: isMobile ? '16px' : '18px' }}>{error}</p>
      </div>
    );
  }

  if (!adminData) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: isMobile ? '10px' : '20px',
        textAlign: 'center'
      }}>
        <p style={{ fontSize: isMobile ? '16px' : '18px' }}>No profile data available</p>
      </div>
    );
  }

  return (
    <div className="profile-page" style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <div className="page-header" style={{ marginBottom: '32px' }}>
        <h2 style={{ fontFamily: 'Outfit', fontSize: '24px', fontWeight: '800', margin: '0', color: 'var(--text-main)' }}>Admin Portfolio</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '4px' }}>Control your account settings and personal identification.</p>
      </div>

      <div className="grid-responsive">
        {/* Left Side: Avatar Card */}
        <div className="admin-card col-4 col-md-12" style={{ padding: '40px 24px', textAlign: 'center', height: 'fit-content' }}>
          <div style={{ position: 'relative', width: '120px', height: '120px', margin: '0 auto 24px' }}>
            <img
              src={adminData.profile_picture || 'https://ui-avatars.com/api/?name=' + adminData.name}
              alt="Profile"
              style={{ width: '100%', height: '100%', borderRadius: '32px', objectFit: 'cover', border: '4px solid var(--bg-main)', boxShadow: 'var(--shadow-md)' }}
            />
            {isEditing && (
              <div style={{ position: 'absolute', bottom: '-8px', right: '-8px', width: '36px', height: '36px', background: 'var(--primary)', color: 'white', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '3px solid white' }}>
                <i className="fas fa-camera" style={{ fontSize: '14px' }}></i>
              </div>
            )}
          </div>
          <h3 style={{ margin: '0', fontSize: '20px', fontWeight: '800', color: 'var(--text-main)' }}>{adminData.name}</h3>
          <p style={{ color: 'var(--primary)', fontWeight: '700', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '2px', margin: '8px 0 24px' }}>{adminData.user_type}</p>

          <div style={{ background: 'var(--bg-main)', padding: '16px', borderRadius: '16px', textAlign: 'left', marginBottom: '24px' }}>
            <div style={{ fontSize: '10px', fontWeight: '800', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '8px' }}>Security Status</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="fas fa-shield-alt" style={{ color: 'var(--success)' }}></i>
              <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-main)' }}>Verified Administrator</span>
            </div>
          </div>

          {!isEditing && (
            <button className="btn btn-outline" style={{ width: '100%', padding: '12px' }} onClick={() => setIsEditing(true)}>
              <i className="fas fa-user-edit" style={{ marginRight: '8px' }}></i> Edit Profile
            </button>
          )}
        </div>

        {/* Right Side: Form Card */}
        <div className="admin-card col-8 col-md-12" style={{ padding: '0', overflow: 'hidden' }}>
          <div style={{ padding: '24px 32px', borderBottom: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: 'var(--text-main)' }}>
              {isEditing ? 'Modify Personal Records' : 'Account Intelligence'}
            </h4>
            {isEditing && (
              <span style={{ fontSize: '11px', color: 'var(--primary)', fontWeight: '700', textTransform: 'uppercase' }}>Editing Mode Active</span>
            )}
          </div>

          <div style={{ padding: '32px' }}>
            {updateError && (
              <div style={{ background: 'var(--error-light)', color: 'var(--error)', padding: '12px 16px', borderRadius: '12px', fontSize: '13px', fontWeight: '600', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i className="fas fa-exclamation-circle"></i> {updateError}
              </div>
            )}

            <div className="grid-responsive">
              <div className="form-group col-6 col-md-12">
                <label style={labelStyle}>IDENTITY NAME</label>
                {isEditing ? (
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} style={inputStyle} />
                ) : (
                  <div style={readOnlyValueStyle}>{adminData.name}</div>
                )}
              </div>

              <div className="form-group col-6 col-md-12">
                <label style={labelStyle}>EMAIL ADDRESS</label>
                {isEditing ? (
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} style={inputStyle} />
                ) : (
                  <div style={readOnlyValueStyle}>{adminData.email}</div>
                )}
              </div>

              <div className="form-group col-6 col-md-12">
                <label style={labelStyle}>PRIMARY CONTACT</label>
                <div style={readOnlyValueStyle}>{adminData.phone_number}</div>
                <p style={{ margin: '4px 0 0', fontSize: '10px', color: 'var(--text-dim)' }}>Locked for security. Use 2FA to change.</p>
              </div>

              <div className="form-group col-6 col-md-12">
                <label style={labelStyle}>DATE OF BIRTH</label>
                {isEditing ? (
                  <input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleInputChange} style={inputStyle} />
                ) : (
                  <div style={readOnlyValueStyle}>{formData.date_of_birth || 'Not Specified'}</div>
                )}
              </div>

              <div className="form-group col-6 col-md-12">
                <label style={labelStyle}>CITY / LOCALITY</label>
                {isEditing ? (
                  <input type="text" name="city" value={formData.city} onChange={handleInputChange} style={inputStyle} />
                ) : (
                  <div style={readOnlyValueStyle}>{adminData.city || 'Not Specified'}</div>
                )}
              </div>

              <div className="form-group col-6 col-md-12">
                <label style={labelStyle}>STATE / REGION</label>
                {isEditing ? (
                  <input type="text" name="state" value={formData.state} onChange={handleInputChange} style={inputStyle} />
                ) : (
                  <div style={readOnlyValueStyle}>{adminData.state || 'Not Specified'}</div>
                )}
              </div>
            </div>

            {isEditing && (
              <div style={{ marginTop: '32px', display: 'flex', gap: '16px' }}>
                <button className="btn btn-primary" style={{ flex: 2, padding: '14px' }} onClick={handleUpdateProfile} disabled={updateLoading}>
                  {updateLoading ? <i className="fas fa-spinner fa-spin"></i> : 'Synchronize Profile'}
                </button>
                <button className="btn btn-outline" style={{ flex: 1, padding: '14px' }} onClick={() => setIsEditing(false)}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const labelStyle = {
  display: 'block',
  fontSize: '10px',
  fontWeight: '800',
  color: 'var(--text-dim)',
  letterSpacing: '1px',
  marginBottom: '8px',
  textTransform: 'uppercase'
};

const inputStyle = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: '12px',
  border: '1px solid var(--border-light)',
  fontSize: '14px',
  fontFamily: 'inherit',
  fontWeight: '600',
  background: '#fcfcfc',
  transition: 'all 0.2s',
  outline: 'none',
  boxSizing: 'border-box'
};

const readOnlyValueStyle = {
  padding: '12px 16px',
  background: 'var(--bg-main)',
  borderRadius: '12px',
  fontSize: '14px',
  fontWeight: '700',
  color: 'var(--text-main)',
  border: '1px solid transparent'
};

export default Profile;